import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Canvas, useFrame, useLoader } from "@react-three/fiber";
import {
  Bounds,
  Center,
  Grid,
  GizmoHelper,
  GizmoViewport,
  OrbitControls,
  TransformControls,
} from "@react-three/drei";
import type { OrbitControls as OrbitControlsImpl } from "three-stdlib";
import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader.js";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader.js";
import { MTLLoader } from "three/examples/jsm/loaders/MTLLoader.js";

export interface Model3DFile {
  url: string;
  type: "stl" | "obj";
  mtlUrl?: string;
  textureUrls?: Record<string, string>;
}

// A single entry in a multi-model scene — `id` identifies it for
// active/ghost highlighting and click-to-select, `label` is an optional
// human-readable name for list/chip UI.
export interface Model3DEntry extends Model3DFile {
  id: string;
  label?: string;
}

export type Model3DRenderMode = "solid" | "wireframe";

// null means plain camera orbit — no per-model gizmo attached.
export type Model3DTransformMode = "translate" | "rotate" | "scale" | null;

// Inactive models in a multi-model scene render as translucent "ghosts" —
// visible enough to compare against the active model, but never competing
// with it for attention. depthWrite is off so a ghost never occludes the
// active model it's overlaid with.
const GHOST_COLOR = "#9a96ab";
const GHOST_OPACITY = 0.16;

// Different scans are authored at wildly different real-world scales (e.g. a
// full leg model in millimeters vs. a small foot model in centimeters). When
// overlaying multiple models for comparison, normalizing each one to the same
// bounding-box size keeps the smaller one from disappearing next to the
// larger one — <Bounds> then frames the normalized group, not the raw model.
const NORMALIZED_SIZE = 2;

function getUniformScale(box: THREE.Box3, targetSize: number): number {
  const size = new THREE.Vector3();
  box.getSize(size);
  const maxDim = Math.max(size.x, size.y, size.z, 0.0001);
  return targetSize / maxDim;
}

function STLModel({
  url,
  color,
  wireframe,
  active,
}: {
  url: string;
  color: string;
  wireframe: boolean;
  active: boolean;
}) {
  const geometry = useLoader(STLLoader, url);
  const meshRef = useRef<THREE.Mesh>(null);

  useEffect(() => {
    geometry?.computeVertexNormals();
  }, [geometry]);

  const scale = useMemo(() => {
    geometry.computeBoundingBox();
    if (!geometry.boundingBox) return 1;
    return getUniformScale(geometry.boundingBox, NORMALIZED_SIZE);
  }, [geometry]);

  return (
    <Center>
      <mesh
        ref={meshRef}
        geometry={geometry}
        scale={scale}
        castShadow={active}
        receiveShadow
      >
        {active ? (
          <meshStandardMaterial
            color={color}
            wireframe={wireframe}
            side={THREE.DoubleSide}
            roughness={0.4}
            metalness={0.2}
          />
        ) : (
          <meshStandardMaterial
            color={GHOST_COLOR}
            wireframe={wireframe}
            side={THREE.DoubleSide}
            transparent
            opacity={GHOST_OPACITY}
            depthWrite={false}
          />
        )}
      </mesh>
    </Center>
  );
}

function OBJModel({
  url,
  color,
  wireframe,
  active,
}: {
  url: string;
  color: string;
  wireframe: boolean;
  active: boolean;
}) {
  const obj = useLoader(OBJLoader, url);
  const cloned = useMemo(() => {
    const c = obj.clone();
    const box = new THREE.Box3().setFromObject(c);
    c.scale.setScalar(getUniformScale(box, NORMALIZED_SIZE));
    return c;
  }, [obj]);
  cloned.traverse((child) => {
    if ((child as THREE.Mesh).isMesh) {
      (child as THREE.Mesh).material = active
        ? new THREE.MeshStandardMaterial({
            color,
            wireframe,
            side: THREE.DoubleSide,
            roughness: 0.4,
            metalness: 0.2,
          })
        : new THREE.MeshStandardMaterial({
            color: GHOST_COLOR,
            wireframe,
            side: THREE.DoubleSide,
            transparent: true,
            opacity: GHOST_OPACITY,
            depthWrite: false,
          });
    }
  });

  return (
    <Center>
      <primitive object={cloned} />
    </Center>
  );
}

// Ported from discovery/3d-model-viewer's OBJModelWithMTL — loads a .mtl material
// file for an .obj, remapping texture references onto the blob URLs of whatever
// image files were uploaded alongside it (matched by lowercased filename).
function OBJModelWithMTL({
  objUrl,
  mtlUrl,
  fallbackColor,
  textureUrls,
  wireframe,
  active,
}: {
  objUrl: string;
  mtlUrl: string;
  fallbackColor: string;
  textureUrls?: Record<string, string>;
  wireframe: boolean;
  active: boolean;
}) {
  const mtl = useLoader(MTLLoader, mtlUrl, (loader) => {
    if (textureUrls && Object.keys(textureUrls).length > 0) {
      const mgr = new THREE.LoadingManager();
      mgr.setURLModifier((url: string) => {
        const filename =
          url.split(/[\\/]/).pop()?.split("?")[0]?.toLowerCase() ?? "";
        return textureUrls[filename] ?? url;
      });
      loader.manager = mgr;
    }
  });
  const obj = useLoader(OBJLoader, objUrl, (loader) => {
    mtl.preload();
    (loader as OBJLoader).setMaterials(mtl);
  });

  // Capture each mesh's original (phong, textured) material once at clone
  // time. The ghost branch below replaces mesh.material in place, and
  // `cloned` is memoized across active/ghost toggles — reading mesh.material
  // directly would pick up the ghost material instead of the original after
  // the first toggle, permanently losing the texture.
  const { cloned, sourceMaterials } = useMemo(() => {
    const c = obj.clone(true);
    const box = new THREE.Box3().setFromObject(c);
    c.scale.setScalar(getUniformScale(box, NORMALIZED_SIZE));
    const materials = new Map<THREE.Mesh, THREE.Material>();
    c.traverse((child) => {
      if (!(child as THREE.Mesh).isMesh) return;
      const mesh = child as THREE.Mesh;
      const mat = Array.isArray(mesh.material)
        ? mesh.material[0]
        : mesh.material;
      materials.set(mesh, mat);
    });
    return { cloned: c, sourceMaterials: materials };
  }, [obj]);
  cloned.traverse((child) => {
    if (!(child as THREE.Mesh).isMesh) return;
    const mesh = child as THREE.Mesh;

    if (!active) {
      mesh.material = new THREE.MeshStandardMaterial({
        color: GHOST_COLOR,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: GHOST_OPACITY,
        depthWrite: false,
        wireframe,
      });
      return;
    }

    const phong = sourceMaterials.get(mesh) as
      THREE.MeshPhongMaterial | undefined;
    const hex = phong?.color?.getHex?.() ?? 0;
    const color =
      phong?.map || hex > 0 ? phong.color : new THREE.Color(fallbackColor);

    mesh.material = new THREE.MeshStandardMaterial({
      color: phong?.map ? new THREE.Color(1, 1, 1) : color,
      map: phong?.map ?? null,
      side: THREE.DoubleSide,
      roughness: 0.4,
      metalness: 0.1,
      transparent: (phong?.opacity ?? 1) < 1,
      opacity: phong?.opacity ?? 1,
      wireframe,
    });
  });

  return (
    <Center>
      <primitive object={cloned} />
    </Center>
  );
}

// Uploaded models can be authored at wildly different scales (a leg scan in
// centimeters vs. a small STL in meters), and <Center> only recenters — it
// doesn't tell us how big the object actually is. Measure the real bounding
// box once the model has settled so the floor grid sits right under the feet
// instead of at a fixed y=-2 that's meaningless for the model's own scale.
function GroundGrid({
  targetRef,
}: {
  targetRef: React.RefObject<THREE.Object3D | null>;
}) {
  const [floor, setFloor] = useState<{
    y: number;
    size: number;
    cellSize: number;
    sectionSize: number;
  } | null>(null);
  const measuredRef = useRef(false);

  useFrame(() => {
    if (measuredRef.current || !targetRef.current) return;
    const box = new THREE.Box3().setFromObject(targetRef.current);
    if (box.isEmpty() || !Number.isFinite(box.min.y)) return;
    const size = new THREE.Vector3();
    box.getSize(size);
    const maxDim = Math.max(size.x, size.y, size.z, 0.001);
    // Scale line spacing to the model's own scale too — otherwise a plane
    // sized for a 100-unit model but drawn with default ~1-unit cell lines
    // renders as thousands of lines that alias into a solid fill.
    setFloor({
      y: box.min.y,
      size: maxDim * 8,
      cellSize: maxDim / 20,
      sectionSize: maxDim / 4,
    });
    measuredRef.current = true;
  });

  if (!floor) return null;

  return (
    <Grid
      position={[0, floor.y, 0]}
      args={[floor.size, floor.size]}
      cellSize={floor.cellSize}
      sectionSize={floor.sectionSize}
      cellColor="rgba(108,99,255,0.35)"
      sectionColor="rgba(108,99,255,0.6)"
      fadeDistance={floor.size * 1.5}
      fadeStrength={1}
    />
  );
}

function ModelLoader({
  model,
  wireframe,
  active,
}: {
  model: Model3DEntry;
  wireframe: boolean;
  active: boolean;
}) {
  if (model.type === "stl") {
    return (
      <STLModel
        url={model.url}
        color="#6c63ff"
        wireframe={wireframe}
        active={active}
      />
    );
  }
  if (model.mtlUrl) {
    return (
      <OBJModelWithMTL
        key={model.url}
        objUrl={model.url}
        mtlUrl={model.mtlUrl}
        fallbackColor="#6c63ff"
        textureUrls={model.textureUrls}
        wireframe={wireframe}
        active={active}
      />
    );
  }
  return (
    <OBJModel
      url={model.url}
      color="#6c63ff"
      wireframe={wireframe}
      active={active}
    />
  );
}

// Lets the user grab the active model directly and move/rotate/scale it in
// place, independent of the other (ghosted) models and of the shared camera.
// Disables OrbitControls for the duration of the drag so the two gestures
// (orbit the camera vs. transform the model) never fight each other.
function ModelTransformGizmo({
  target,
  mode,
  orbitControlsRef,
}: {
  target: THREE.Object3D | null;
  mode: "translate" | "rotate" | "scale";
  orbitControlsRef: React.RefObject<OrbitControlsImpl | null>;
}) {
  const transformRef =
    useRef<React.ComponentRef<typeof TransformControls>>(null);

  useEffect(() => {
    // three-stdlib's TransformControls dispatches "dragging-changed" and
    // "objectChange" events at runtime that aren't part of its typed
    // Object3DEventMap — cast to the shape we actually need instead of
    // fighting the upstream types.
    const controls = transformRef.current as unknown as {
      axis: string | null;
      addEventListener(
        type: "dragging-changed",
        cb: (event: { value: boolean }) => void,
      ): void;
      addEventListener(type: "objectChange", cb: () => void): void;
      removeEventListener(
        type: "dragging-changed",
        cb: (event: { value: boolean }) => void,
      ): void;
      removeEventListener(type: "objectChange", cb: () => void): void;
    } | null;
    if (!controls) return;

    const draggingChanged = (event: { value: boolean }) => {
      if (orbitControlsRef.current) {
        orbitControlsRef.current.enabled = !event.value;
      }
    };
    controls.addEventListener("dragging-changed", draggingChanged);

    // Grabbing a single axis handle would otherwise scale that axis alone,
    // stretching the model out of proportion. Whichever handle is dragged
    // (X, Y, Z, or the uniform center one), snap all three axes to match it
    // so resizing always keeps the model's proportions.
    const objectChange = () => {
      if (mode !== "scale" || !target || !controls.axis) return;
      const s = target.scale;
      const uniform = controls.axis.includes("X")
        ? s.x
        : controls.axis.includes("Y")
          ? s.y
          : s.z;
      s.set(uniform, uniform, uniform);
    };
    controls.addEventListener("objectChange", objectChange);

    return () => {
      controls.removeEventListener("dragging-changed", draggingChanged);
      controls.removeEventListener("objectChange", objectChange);
    };
  }, [orbitControlsRef, mode, target]);

  if (!target) return null;

  return <TransformControls ref={transformRef} object={target} mode={mode} />;
}

// Ported from Frontend/discovery/3d-model-viewer/app/components/ModelViewer.tsx —
// trimmed down for this intake flow (no zustand store, no settings panel, no
// auto-rotate), but keeps STL, plain OBJ, OBJ+MTL(+textures) support and the
// solid/wireframe render-mode toggle. Supports multiple models overlaid in the
// same scene (each is independently re-centered onto the origin by <Center>,
// so they naturally stack for comparison) — the active one renders at full
// color, the rest render as translucent ghosts. Click any model to make it
// active.
export default function Model3DViewer({
  models,
  activeId,
  onSelectModel,
  renderMode = "solid",
  transformMode = null,
}: {
  models: Model3DEntry[];
  activeId: string;
  onSelectModel?: (id: string) => void;
  renderMode?: Model3DRenderMode;
  transformMode?: Model3DTransformMode;
}) {
  const allModelsGroupRef = useRef<THREE.Group>(null);
  const orbitControlsRef = useRef<OrbitControlsImpl | null>(null);
  const modelGroupRefs = useRef(new Map<string, THREE.Group>());
  const modelsKey = models.map((m) => m.id).join(",");
  // Render the active model last so it draws on top of any overlapping ghosts.
  const orderedModels = [...models].sort(
    (a, b) => Number(a.id === activeId) - Number(b.id === activeId),
  );

  return (
    <Canvas
      camera={{ position: [0, 0, 5], fov: 50 }}
      shadows
      gl={{ antialias: true, alpha: false }}
    >
      <color attach="background" args={["#f5f3ef"]} />
      <ambientLight intensity={0.9} />
      <hemisphereLight args={["#ffffff", "#6c6357", 0.6]} />
      <directionalLight
        position={[10, 10, 10]}
        intensity={2.2}
        castShadow
        shadow-mapSize={[2048, 2048]}
      />
      <directionalLight position={[-10, -5, -10]} intensity={0.8} />
      <pointLight position={[0, 10, 0]} intensity={1} />

      <Suspense fallback={null}>
        <Bounds fit clip observe margin={1.2}>
          <group ref={allModelsGroupRef} key={modelsKey}>
            {orderedModels.map((model) => (
              <group
                key={model.id}
                ref={(el) => {
                  if (el) modelGroupRefs.current.set(model.id, el);
                  else modelGroupRefs.current.delete(model.id);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectModel?.(model.id);
                }}
                onPointerOver={(e) => {
                  e.stopPropagation();
                  document.body.style.cursor = "pointer";
                }}
                onPointerOut={() => {
                  document.body.style.cursor = "auto";
                }}
              >
                <ModelLoader
                  model={model}
                  wireframe={renderMode === "wireframe"}
                  active={model.id === activeId}
                />
              </group>
            ))}
          </group>
        </Bounds>
        <GroundGrid key={modelsKey} targetRef={allModelsGroupRef} />
        {transformMode && (
          <ModelTransformGizmo
            target={modelGroupRefs.current.get(activeId) ?? null}
            mode={transformMode}
            orbitControlsRef={orbitControlsRef}
          />
        )}
      </Suspense>

      <OrbitControls
        ref={orbitControlsRef}
        enableDamping
        dampingFactor={0.05}
        minDistance={0.5}
        maxDistance={50}
        makeDefault
      />

      <GizmoHelper alignment="bottom-right" margin={[50, 50]}>
        <GizmoViewport
          axisColors={["#ff4d4d", "#3ddc84", "#6c63ff"]}
          labelColor="white"
        />
      </GizmoHelper>
    </Canvas>
  );
}
